import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ExternalLink, Pencil, PlayCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { trainingsplanerAPI } from '@/lib/api';

/* Übungsbibliothek (13.10.2026, replica/coaching-recherche/gap.md W1): je Übungsname Anleitung, Video-Link, Muskeln
   und Gerät. Die Mitglieder sehen es in der App zur geplanten Übung. Oben stehen die im Planer verwendeten Übungen
   ohne Eintrag. Dieselbe Liste im CRM (/planer) und auf /coach — Aufrufe als Props. */

const fehlerText = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);

function Formular({ start, muskeln, geraete, kategorien = [], onSpeichern, onAbbrechen, busy }) {
  const [f, setF] = useState(start);
  const umschalten = (m) => setF({ ...f, muskeln: f.muskeln.includes(m) ? f.muskeln.filter((x) => x !== m) : [...f.muskeln, m] });
  return (
    <div className="space-y-2 rounded-md border border-border p-3" data-testid="bibliothek-formular">
      <p className="text-sm font-bold break-words">{f.name}</p>
      <Textarea aria-label="Anleitung" rows={3} maxLength={1500} placeholder="Ausführung in zwei, drei Sätzen: Stand, Bewegung, worauf achten"
        value={f.anleitung} onChange={(e) => setF({ ...f, anleitung: e.target.value })} />
      <Input aria-label="Video-Link" placeholder="https://youtu.be/… oder https://vimeo.com/…" value={f.video_url}
        onChange={(e) => setF({ ...f, video_url: e.target.value })} />
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Muskeln">
        {muskeln.map((m) => (
          <button key={m} type="button" aria-pressed={f.muskeln.includes(m)} onClick={() => umschalten(m)}
            className={`rounded-full border px-2.5 py-1 text-xs ${f.muskeln.includes(m) ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-muted'}`}>{m}</button>
        ))}
      </div>
      <select aria-label="Gerät" className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm" value={f.geraet || ''}
        onChange={(e) => setF({ ...f, geraet: e.target.value || null })}>
        <option value="">Gerät …</option>
        {geraete.map((g) => <option key={g} value={g}>{g}</option>)}
      </select>
      {kategorien.length > 0 && (
        <select aria-label="Kategorie" className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm" value={f.kategorie || ''}
          onChange={(e) => setF({ ...f, kategorie: e.target.value || null })}>
          <option value="">Kategorie …</option>
          {kategorien.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onAbbrechen}>Abbrechen</Button>
        <Button size="sm" disabled={busy} onClick={() => onSpeichern(f)}>Speichern</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Alles leeren und speichern entfernt den Eintrag. Videos nur verlinken (YouTube, Vimeo oder eigene Seite).</p>
    </div>
  );
}

export default function Uebungsbibliothek({ laden, speichern, darfAendern }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [offen, setOffen] = useState(null);     // Name, der gerade bearbeitet wird
  const [busy, setBusy] = useState(false);
  const lade = useCallback(() => {
    laden().then((r) => { setD(r.data); setErr(''); }).catch((e) => setErr(fehlerText(e, 'Bibliothek nicht geladen')));
  }, [laden]);
  useEffect(() => { lade(); }, [lade]);
  const darf = darfAendern ?? d?.darf_aendern ?? true;

  const zeilen = useMemo(() => {
    if (!d) return [];
    const s = q.trim().toLowerCase();
    const alle = [...d.offen.map((name) => ({ name, leer: true })), ...d.eintraege];
    return s ? alle.filter((z) => z.name.toLowerCase().includes(s)) : alle;
  }, [d, q]);

  const sichern = async (f) => {
    setBusy(true);
    try {
      await speichern({ name: f.name, anleitung: f.anleitung, video_url: f.video_url || null, muskeln: f.muskeln, geraet: f.geraet || null,
        kategorie: f.kategorie || null });
      toast.success('Gespeichert – die App zeigt es zur Übung'); setOffen(null); lade();
    } catch (e) { toast.error(fehlerText(e, 'Nicht gespeichert')); }
    setBusy(false);
  };

  if (err) return <p className="text-sm text-destructive" role="alert">{err}</p>;
  if (!d) return <Skeleton className="h-32" />;
  return (
    <div className="space-y-3" data-testid="bibliothek">
      <div className="flex flex-wrap items-center gap-2">
        <Input aria-label="Übung suchen" placeholder="Übung suchen" className="max-w-xs" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="text-xs text-muted-foreground">{d.eintraege.length} mit Anleitung · {d.offen.length} im Plan ohne</span>
      </div>
      {!zeilen.length && <p className="text-sm text-muted-foreground">Keine Übung gefunden.</p>}
      <ul className="divide-y divide-border/60">
        {zeilen.map((z) => (
          <li key={z.id || `leer-${z.name}`} className="py-2.5">
            {offen === z.name ? (
              <Formular start={{ name: z.name, anleitung: z.anleitung || '', video_url: z.video_url || '', muskeln: z.muskeln || [], geraet: z.geraet || null,
                kategorie: z.kategorie || null }}
                muskeln={d.muskeln} geraete={d.geraete} kategorien={d.kategorien || []} busy={busy} onSpeichern={sichern} onAbbrechen={() => setOffen(null)} />
            ) : (
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                    <span className="break-words">{z.name}</span>
                    {z.leer && <Badge variant="outline" className="text-[10px]">noch ohne Anleitung</Badge>}
                    {z.geraet && <Badge variant="secondary" className="text-[10px]">{z.geraet}</Badge>}
                    {z.kategorie && <Badge variant="outline" className="text-[10px]">{(d.kategorien || []).find((k) => k.key === z.kategorie)?.label || z.kategorie}</Badge>}
                  </p>
                  {z.anleitung && <p className="mt-0.5 text-sm text-muted-foreground break-words">{z.anleitung}</p>}
                  {(z.muskeln || []).length > 0 && <p className="mt-0.5 text-xs text-muted-foreground">{z.muskeln.join(' · ')}</p>}
                  {z.video_url && (
                    <a href={z.video_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                      {z.video_art === 'link' ? <ExternalLink className="h-3.5 w-3.5" /> : <PlayCircle className="h-3.5 w-3.5" />} Video ansehen
                    </a>
                  )}
                </div>
                {darf && (
                  <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => setOffen(z.name)} data-testid={`bibliothek-bearbeiten-${z.name}`}>
                    <Pencil className="h-3.5 w-3.5" /> {z.leer ? 'Anlegen' : 'Ändern'}
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* CRM: Karte unter /planer (lesen mit trainingsplaner.view, pflegen mit .edit). */
const crmLaden = () => trainingsplanerAPI.bibliothek();
const crmSpeichern = (body) => trainingsplanerAPI.bibliothekSetzen(body);

export function UebungsbibliothekKarte() {
  const [auf, setAuf] = useState(false);
  return (
    <Card className="border-border/60 print:hidden" data-testid="bibliothek-karte">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <div>
          <CardTitle className="text-base">Übungsbibliothek</CardTitle>
          <p className="text-xs text-muted-foreground">Anleitung und Video je Übung – Mitglieder sehen beides in der App zur geplanten Übung.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAuf((v) => !v)} aria-expanded={auf}>{auf ? 'Zuklappen' : 'Öffnen'}</Button>
      </CardHeader>
      {auf && <CardContent><Uebungsbibliothek laden={crmLaden} speichern={crmSpeichern} /></CardContent>}
    </Card>
  );
}
